import { QueryOptionsFactory } from "@/features/company/utils";
import {
  AliasMailSchema,
  CreateAliasMailDto,
  DeleteAliasMailResponseDto
} from "@/types/user/alias-mail.types";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from "@tanstack/react-query";
import { AuthApiClient } from "../api-client/api-client";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useAppSelector } from "@/redux/hooks";
import { getUserRoles } from "@/redux/slices/userSlice";
import { role } from "@/features/auth/types";

export const useAliasMail = () => {
  const queryClient = useQueryClient();
  const { leUuid: legalEntityUuid } = useCompanyContext();
  const roles = useAppSelector(getUserRoles);
  const isAdmin = roles.includes(role.ADMIN);

  // GET all alias mail from real //
  const useGetAliasMail = (options?: Omit<UseQueryOptions<AliasMailSchema[]>, "queryKey">) =>
    useQuery<AliasMailSchema[]>({
      ...QueryOptionsFactory.aliasMail(legalEntityUuid),
      ...options,
      queryFn: () => AuthApiClient.getAllAliasMail(legalEntityUuid)
    });

  // DELETE alias mail from real //
  const useDeleteAliasMail = (
    options?: UseMutationOptions<DeleteAliasMailResponseDto, Error, DeleteAliasMailResponseDto>
  ) => {
    return useMutation<DeleteAliasMailResponseDto, Error, DeleteAliasMailResponseDto>({
      mutationFn: async (data: DeleteAliasMailResponseDto) =>
        await AuthApiClient.deleteAliasMail(data.uuid, legalEntityUuid),
      onSuccess: (response: DeleteAliasMailResponseDto) => {
        if (legalEntityUuid) {
          queryClient.invalidateQueries({
            queryKey: QueryOptionsFactory.legalEntity(legalEntityUuid ?? "", isAdmin).queryKey
          });
        } else {
          queryClient.invalidateQueries({ queryKey: ["user", "profile"] }); // we still invalidate profile to avoid bugs for now (TODO remove)
        }

        const querykey = QueryOptionsFactory.aliasMail(legalEntityUuid).queryKey;
        queryClient.setQueryData(querykey, (oldData: AliasMailSchema[] | undefined) => {
          return oldData?.filter((element) => element.uuid !== response.uuid);
        });
      },
      ...options
    });
  };

  // POST alias mail to real //
  const useCreateAliasMail = (
    options?: UseMutationOptions<AliasMailSchema, Error, CreateAliasMailDto>
  ) => {
    return useMutation<AliasMailSchema, Error, CreateAliasMailDto>({
      mutationFn: async (aliasMail: CreateAliasMailDto) =>
        await AuthApiClient.createAliasMail(aliasMail, legalEntityUuid),
      onSuccess: (response: AliasMailSchema) => {
        if (legalEntityUuid) {
          queryClient.invalidateQueries({
            queryKey: QueryOptionsFactory.legalEntity(legalEntityUuid ?? "", isAdmin).queryKey
          });
        } else {
          queryClient.invalidateQueries({ queryKey: ["user", "profile"] }); // we still invalidate profile to avoid bugs for now (TODO remove)
        }

        const querykey = QueryOptionsFactory.aliasMail(legalEntityUuid).queryKey;
        queryClient.setQueryData(querykey, (queryData: AliasMailSchema[] | undefined) => {
          if (queryData) {
            if (response.main) {
              queryData.map((element) => (element.main = false));
            }
            queryData.push(response);
          }
          return queryData;
        });
      },
      ...options
    });
  };

  // PATCH alias mail public //
  type SetPublicAliasMailBody = {
    aliasMailUuid: string;
    public: boolean;
  };
  const useSetPublicAliasMail = (
    options?: UseMutationOptions<AliasMailSchema, Error, SetPublicAliasMailBody>
  ) => {
    return useMutation<AliasMailSchema, Error, SetPublicAliasMailBody>({
      mutationFn: async (data: SetPublicAliasMailBody) =>
        await AuthApiClient.setPublicAliasMail(data.aliasMailUuid, legalEntityUuid, data.public),
      onSuccess: (response: AliasMailSchema) => {
        if (legalEntityUuid) {
          queryClient.invalidateQueries({
            queryKey: QueryOptionsFactory.legalEntity(legalEntityUuid ?? "", isAdmin).queryKey
          });
        } else {
          queryClient.invalidateQueries({ queryKey: ["user", "profile"] }); // we still invalidate profile to avoid bugs for now (TODO remove)
        }

        const querykey = QueryOptionsFactory.aliasMail(legalEntityUuid).queryKey;
        queryClient.setQueryData(querykey, (oldData: AliasMailSchema[] | undefined) => {
          if (!oldData) return;
          for (const aliasMail of oldData) {
            if (aliasMail.uuid === response.uuid) {
              aliasMail.public = response.public;
            } else {
              aliasMail.public = false;
            }
          }
          return oldData;
        });
      },
      ...options
    });
  };

  return { useGetAliasMail, useDeleteAliasMail, useCreateAliasMail, useSetPublicAliasMail };
};
